import { useState } from 'react'
import { Box, CircularProgress, TextField, Typography } from '@mui/material'
import { ArrowDown, ArrowUp, Search } from 'lucide-react'
import { inspectQuery, useNavigate } from '../../lib/router'
import type { PlotRow, Run } from '../../api/types'
import { usePlotRows } from '../../api/hooks'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '../../components/primitives'
import { Skeleton, SkeletonGridRows } from '../../components/Skeleton'
import { ha, num } from '../../lib/format'
import { tokens } from '../../theme'

interface Column {
  key: string
  label: string
  width: string
  /** Only the columns the endpoint allows sorting on. */
  sortable?: boolean
  mono?: boolean
  align?: 'right'
}

const COLUMNS: Column[] = [
  { key: 'plot_id', label: 'Plot_ID', width: '150px', sortable: true, mono: true },
  { key: 'status', label: 'Status', width: '110px', sortable: true },
  { key: 'issues', label: 'Issues', width: '80px', sortable: true, align: 'right' },
  { key: 'area_ha', label: 'Area', width: '100px', sortable: true, align: 'right' },
  { key: 'co', label: 'CO', width: '60px' },
  { key: 'su', label: 'SU', width: 'minmax(150px,1.2fr)' },
  { key: 'fo', label: 'FO', width: 'minmax(170px,1.2fr)' },
  { key: 'fo_id', label: 'FO_ID', width: '110px', sortable: true, mono: true },
  { key: 'farmer_id', label: 'Farmer_ID', width: '120px', sortable: true, mono: true },
  { key: 'yield_kg', label: 'Yield', width: '90px', sortable: true, align: 'right' },
  { key: 'certificate', label: 'Certificate', width: '160px', mono: true },
]

const GRID = COLUMNS.map((c) => c.width).join(' ')
const PAGE = 100

/**
 * Every plot and every field, filtered exactly as the map is. The map answers
 * "where", this answers "which" — and it is the quickest way to find one plot
 * among thousands spread across a country.
 */
export function ResultsTable({
  run, checkFilter, onClearFilter,
}: { run: Run; checkFilter: string[]; onClearFilter: () => void }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<string>('ordinal')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')

  const { data, isFetching } = usePlotRows(run.id, {
    check: checkFilter.join(',') || undefined,
    q: search.trim() || undefined,
    sort,
    dir,
    limit: String(PAGE),
    offset: String(page * PAGE),
  })

  const rows = data?.plots ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))

  const toggleSort = (key: string) => {
    setPage(0)
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc')
    else {
      setSort(key)
      setDir('asc')
    }
  }

  const cell = {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    borderTop: `1px solid ${tokens.color.ink200}`,
    minWidth: 0,
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', px: 6, py: 6, gap: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6 }}>
        <Box>
          <Eyebrow sx={{ mb: 2 }}>
            {checkFilter.length
              ? `Filtered by ${checkFilter.map((k) => run.summary?.checks[k]?.label ?? k).join(', ')}`
              : 'All polygons'}
          </Eyebrow>
          {/* "0 polygons" while the count is still being fetched is not a
              neutral placeholder — it is a wrong answer. */}
          {data ? (
            <Typography variant="h1">
              {num(total)} {total === 1 ? 'polygon' : 'polygons'}
            </Typography>
          ) : (
            <Skeleton width={220} height={30} radius={tokens.radius.md} sx={{ my: 1 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {checkFilter.length > 0 && (
            <Box
              component="button"
              type="button"
              onClick={onClearFilter}
              sx={{
                border: `1px solid ${tokens.color.ink300}`, borderRadius: `${tokens.radius.md}px`,
                height: 36, px: 3, cursor: 'pointer', backgroundColor: 'background.paper',
                font: `500 13px ${tokens.font.sans}`, color: 'text.primary',
              }}
            >
              Clear filter
            </Box>
          )}
          <TextField
            size="small"
            value={search}
            placeholder="Plot, farmer, organisation, certificate"
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            inputProps={{ 'aria-label': 'Search plots' }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', pr: 2, color: 'text.disabled' }}>
                  <Search size={16} strokeWidth={1.5} />
                </Box>
              ),
            }}
            sx={{ width: 320 }}
          />
        </Box>
      </Box>

      <Surface sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 4, px: 5, minWidth: 1500 }}>
            {COLUMNS.map((c) => (
              <Box
                key={c.key}
                component={c.sortable ? 'button' : 'div'}
                type={c.sortable ? 'button' : undefined}
                onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, height: 36, border: 0, p: 0,
                  background: 'none', textAlign: 'left',
                  justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                  cursor: c.sortable ? 'pointer' : 'default',
                  position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: sort === c.key ? 'text.primary' : 'text.disabled',
                }}
              >
                {c.label}
                {sort === c.key &&
                  (dir === 'asc' ? <ArrowUp size={11} strokeWidth={2} /> : <ArrowDown size={11} strokeWidth={2} />)}
              </Box>
            ))}

            {/* Only before anything has arrived: a refetch keeps the rows it
                already has, so the table never blinks between pages. */}
            {!data && (
              <SkeletonGridRows
                columns={COLUMNS.length}
                rows={10}
                height={44}
                align={COLUMNS.map((c, i) => (c.align === 'right' ? i : -1)).filter((i) => i >= 0)}
              />
            )}
            {rows.map((row) => (
              <Row key={row.id} row={row} cell={cell} onOpen={() => navigate(`/checks/runs/${run.id}/plots/${row.id}${inspectQuery(checkFilter)}`)} />
            ))}
          </Box>

          {data && !isFetching && rows.length === 0 && (
            <EmptyState
              title="No polygons match"
              body="Clear the filter or the search to see the rest of the region."
            />
          )}
        </Box>

        <Box
          sx={{
            flex: 'none', height: 48, borderTop: `1px solid ${tokens.color.ink200}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {isFetching ? (
              <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
            ) : (
              `${num(page * PAGE + 1)}–${num(Math.min((page + 1) * PAGE, total))} of ${num(total)}`
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <PageButton disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</PageButton>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {num(page + 1)} / {num(pages)}
            </Typography>
            <PageButton disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</PageButton>
          </Box>
        </Box>
      </Surface>
    </Box>
  )
}

function Row({ row, cell, onOpen }: { row: PlotRow; cell: object; onOpen: () => void }) {
  const text = (v: unknown, mono = false) => (
    <Box
      sx={{
        ...cell,
        font: `400 13px ${mono ? tokens.font.mono : tokens.font.sans}`,
        color: v === null || v === undefined || v === '' ? 'text.disabled' : 'text.primary',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
        lineHeight: '44px',
      }}
    >
      {v === null || v === undefined || v === '' ? '—' : String(v)}
    </Box>
  )
  const right = (v: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
  )

  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      sx={{
        display: 'contents',
        cursor: 'pointer',
        '&:hover > *': { backgroundColor: tokens.color.ink50 },
      }}
    >
      <Box sx={{ ...cell }}>
        <Mono sx={{ fontSize: 13, fontWeight: 500 }}>{row.plot_id ?? '—'}</Mono>
      </Box>
      <Box sx={cell}><StatusPill status={row.status} /></Box>
      {right(
        <Typography sx={{ font: `500 13px ${tokens.font.sans}`, color: row.issue_count ? 'text.primary' : 'text.disabled' }}>
          {row.issue_count || '—'}
        </Typography>,
      )}
      {right(<Typography variant="body2" sx={{ color: 'text.primary' }}>{ha(row.area_ha)}</Typography>)}
      {text(row.co)}
      {text(row.su)}
      {text(row.fo)}
      {text(row.fo_id, true)}
      {text(row.farmer_id, true)}
      {right(<Typography variant="body2" sx={{ color: 'text.primary' }}>{row.yield_kg === null ? '—' : num(row.yield_kg)}</Typography>)}
      {text(row.certificate, true)}
    </Box>
  )
}

function PageButton({ children, disabled, onClick }: { children: React.ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        height: 28, px: 2.5, borderRadius: `${tokens.radius.md}px`,
        border: `1px solid ${tokens.color.ink300}`, backgroundColor: 'background.paper',
        font: `500 12px ${tokens.font.sans}`,
        color: disabled ? 'text.disabled' : 'text.primary',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </Box>
  )
}
